import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Slider,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Filter,
  X as CloseIcon,
  RefreshCw,
  Tag as TagIcon,
  MapPin,
  Euro,
  Settings,
  Calendar,
  User,
  FileText,
  AlertTriangle,
  Power,
  Building,
} from 'lucide-react';

import { 
  CATEGORIES, 
  LOCATIONS, 
  SECOND_LOCATIONS,
  VENDORS 
} from '../constants/inventoryConstants';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: any) => void;
}

// Match the expected filter structure from CoolerInventoryManager
interface FilterState {
  name: string;
  category: string[];              // Fixed: was 'categories'
  tags: string[];
  location: string[];              // Fixed: was 'locations'
  secondLocation: string;
  vendor: string[];                // Fixed: was 'vendors'
  purchaseDateRange: {
    start: string;
    end: string;
  };
  valueRange: {
    min: number;
    max: number;
  };
  requiresPower: boolean | null;
  powerLocation: string;
  isInsured: boolean | null;
  maintenanceStatus: string[];
  hasDocuments: boolean | null;
  hasRelatedEquipment: boolean | null;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  onApplyFilters,
}) => {
  const [filters, setFilters] = useState<FilterState>({
    name: '',
    category: [],                  // Fixed: matches CoolerInventoryManager
    tags: [],
    location: [],                  // Fixed: matches CoolerInventoryManager
    secondLocation: '',
    vendor: [],                    // Fixed: matches CoolerInventoryManager
    purchaseDateRange: {
      start: '',
      end: '',
    },
    valueRange: {
      min: 0,
      max: 10000,                  // Increased max to match slider
    },
    requiresPower: null,
    powerLocation: '',
    isInsured: null,
    maintenanceStatus: [],
    hasDocuments: null,
    hasRelatedEquipment: null,
  });
  
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  // Helper to determine if a filter value is considered "active"
  const isFilterActive = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value.start !== undefined && value.end !== undefined) {
      return value.start !== '' || value.end !== '';
    }
    if (typeof value === 'object' && value.min !== undefined && value.max !== undefined) {
      return value.min > 0 || value.max < 10000;
    }
    if (typeof value === 'boolean') return true;
    return true;
  };

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));

    // Update active filters tracking
    const isActive = isFilterActive(value);
    setActiveFilters(prev => {
      if (isActive && !prev.includes(field)) {
        return [...prev, field];
      } else if (!isActive && prev.includes(field)) {
        return prev.filter(f => f !== field);
      }
      return prev;
    });
  };

  const handleRemoveFilter = (field: keyof FilterState) => {
    const resetValue = Array.isArray(filters[field]) ? [] : 
                      typeof filters[field] === 'object' && filters[field] !== null ?
                        (field === 'valueRange' ? { min: 0, max: 10000 } :
                         field === 'purchaseDateRange' ? { start: '', end: '' } : null) :
                      typeof filters[field] === 'string' ? '' : null;

    setFilters(prev => ({
      ...prev,
      [field]: resetValue
    }));
    
    setActiveFilters(prev => prev.filter(f => f !== field));
  };

  const handleApply = () => {
    console.log('Raw filters before processing:', filters);
    
    // Build active filter object with only meaningful values
    const activeFilterValues = Object.entries(filters).reduce((acc, [key, value]) => {
      console.log(`Processing key: ${key}, value:`, value);
      
      if (isFilterActive(value)) {
        console.log(`Adding to activeFilters - key: ${key}, value:`, value);
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    console.log('Final activeFilterValues:', activeFilterValues);
    onApplyFilters(activeFilterValues);
  };

  const handleReset = () => {
    const initialState: FilterState = {
      name: '',
      category: [],
      tags: [],
      location: [],
      secondLocation: '',
      vendor: [],
      purchaseDateRange: {
        start: '',
        end: '',
      },
      valueRange: {
        min: 0,
        max: 10000,
      },
      requiresPower: null,
      powerLocation: '',
      isInsured: null,
      maintenanceStatus: [],
      hasDocuments: null,
      hasRelatedEquipment: null,
    };
  
    console.log('FilterDrawer: Resetting to initial state');
    setFilters(initialState);
    setActiveFilters([]);
    
    // Actually call the parent to clear filters - this was missing!
    onApplyFilters({});
    
    // Also close the drawer after reset
    onClose();
  };

  // Helper to format field names for display
  const formatFieldName = (field: string): string => {
    const fieldMap: Record<string, string> = {
      name: 'Name',
      category: 'Categories',
      tags: 'Tags',
      location: 'Locations',
      secondLocation: 'Secondary Location',
      vendor: 'Vendors',
      purchaseDateRange: 'Purchase Date',
      valueRange: 'Value Range',
      requiresPower: 'Requires Power',
      powerLocation: 'Power Location',
      isInsured: 'Insured',
      maintenanceStatus: 'Maintenance Status',
      hasDocuments: 'Has Documents',
      hasRelatedEquipment: 'Has Related Equipment'
    };
    return fieldMap[field] || field.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380 }
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Filter size={24} />
          <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
            Filters
          </Typography>
          <IconButton onClick={handleReset} title="Reset filters">
            <RefreshCw size={20} />
          </IconButton>
          <IconButton onClick={onClose}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Active Filters ({activeFilters.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {activeFilters.map(filter => (
                <Chip
                  key={filter}
                  label={formatFieldName(filter)}
                  onDelete={() => handleRemoveFilter(filter as keyof FilterState)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Filter Sections */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Stack spacing={3}>
            {/* Basic Info */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Basic Info
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Name"
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  size="small"
                  placeholder="Search by name..."
                />
                
                <Autocomplete
                  multiple
                  options={CATEGORIES}
                  value={filters.category}
                  onChange={(_, newValue) => handleFilterChange('category', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categories"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <TagIcon size={20} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />

                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={filters.tags}
                  onChange={(_, newValue) => handleFilterChange('tags', newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Tags" 
                      size="small"
                      placeholder="Add tags..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />
              </Stack>
            </Box>

            <Divider />

            {/* Location */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Location
              </Typography>
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={LOCATIONS}
                  value={filters.location}
                  onChange={(_, newValue) => handleFilterChange('location', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Primary Location"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <MapPin size={20} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />

                <Autocomplete
                  options={SECOND_LOCATIONS}
                  value={filters.secondLocation}
                  onChange={(_, newValue) => handleFilterChange('secondLocation', newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Shelf/Drawer Location"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <MapPin size={20} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Purchase Info */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Purchase Info
              </Typography>
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={VENDORS}
                  value={filters.vendor}
                  onChange={(_, newValue) => handleFilterChange('vendor', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendors"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Building size={20} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                />

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Purchase Date Range
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      type="date"
                      label="From"
                      value={filters.purchaseDateRange.start}
                      onChange={(e) => handleFilterChange('purchaseDateRange', {
                        ...filters.purchaseDateRange,
                        start: e.target.value
                      })}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={16} />
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      type="date"
                      label="To"
                      value={filters.purchaseDateRange.end}
                      onChange={(e) => handleFilterChange('purchaseDateRange', {
                        ...filters.purchaseDateRange,
                        end: e.target.value
                      })}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={16} />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Value Range (€{filters.valueRange.min} - €{filters.valueRange.max})
                  </Typography>
                  <Slider
                    value={[filters.valueRange.min, filters.valueRange.max]}
                    onChange={(_, newValue: number | number[]) => {
                      const [min, max] = Array.isArray(newValue) ? newValue : [newValue, newValue];
                      handleFilterChange('valueRange', { min, max });
                    }}
                    valueLabelDisplay="auto"
                    min={0}
                    max={10000}
                    step={100}
                    valueLabelFormat={(value) => `€${value}`}
                    sx={{ mt: 2, mb: 1 }}
                  />
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      label="Min"
                      type="number"
                      value={filters.valueRange.min}
                      onChange={(e) => handleFilterChange('valueRange', {
                        ...filters.valueRange,
                        min: Number(e.target.value)
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>
                      }}
                    />
                    <TextField
                      size="small"
                      label="Max"
                      type="number"
                      value={filters.valueRange.max}
                      onChange={(e) => handleFilterChange('valueRange', {
                        ...filters.valueRange,
                        max: Number(e.target.value)
                      })}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">€</InputAdornment>
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Status & Configuration */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Status & Configuration
              </Typography>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.requiresPower === true}
                      onChange={(e) => handleFilterChange('requiresPower', e.target.checked ? true : null)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Power size={16} />
                      Requires Power
                    </Box>
                  }
                />

                {filters.requiresPower && (
                  <Autocomplete
                    options={LOCATIONS}
                    value={filters.powerLocation}
                    onChange={(_, newValue) => handleFilterChange('powerLocation', newValue || '')}
                    renderInput={(params) => (
                      <TextField {...params} label="Power Location" size="small" />
                    )}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.isInsured === true}
                      onChange={(e) => handleFilterChange('isInsured', e.target.checked ? true : null)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Settings size={16} />
                      Insured
                    </Box>
                  }
                />

                <Autocomplete
                  multiple
                  options={['ok', 'due', 'overdue']}
                  value={filters.maintenanceStatus}
                  onChange={(_, newValue) => handleFilterChange('maintenanceStatus', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Maintenance Status"
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <AlertTriangle size={20} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const color = option === 'overdue' ? 'error' : 
                                   option === 'due' ? 'warning' : 'success';
                      return (
                        <Chip
                          variant="outlined"
                          label={option}
                          size="small"
                          color={color}
                          {...getTagProps({ index })}
                        />
                      );
                    })
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.hasDocuments === true}
                      onChange={(e) => handleFilterChange('hasDocuments', e.target.checked ? true : null)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileText size={16} />
                      Has Documents
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.hasRelatedEquipment === true}
                      onChange={(e) => handleFilterChange('hasRelatedEquipment', e.target.checked ? true : null)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <User size={16} />
                      Has Related Equipment
                    </Box>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Apply Button */}
        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Stack spacing={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleApply}
              disabled={activeFilters.length === 0}
              sx={{ 
                height: 48,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Apply {activeFilters.length > 0 ? `${activeFilters.length} ` : ''}Filter{activeFilters.length !== 1 ? 's' : ''}
            </Button>
            
            {activeFilters.length > 0 && (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                size="small"
              >
                Clear All Filters
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;